import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ShieldCheck, FileText, Lock, Cookie, Sparkles, ArrowLeft, ArrowRight, Mail } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

/* ============================ shared shell ============================ */

const NAV = [
  { to: "/trust/privacy", label: "Privacy Policy", icon: ShieldCheck },
  { to: "/trust/terms", label: "Terms of Service", icon: FileText },
  { to: "/trust/security", label: "Security", icon: Lock },
  { to: "/trust/cookies", label: "Cookie Policy", icon: Cookie },
  { to: "/trust/ai-transparency", label: "AI Transparency", icon: Sparkles },
];

const LAST_UPDATED = "February 4, 2026";
const CONTACT_EMAIL = "hello@intuiterview.com";
const COMPANY = "Intuiterview";

function TrustLayout({ title, kicker, children, testid }) {
  const location = useLocation();
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 relative">
      {/* Ambient background, matches Landing aesthetic */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 h-96 w-96 rounded-full bg-amber-500/[0.05] blur-3xl" />
        <div className="absolute top-40 left-0 h-80 w-80 rounded-full bg-amber-500/[0.03] blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative z-10 px-8 py-6 flex items-center justify-between border-b border-zinc-900">
        <BrandLogo variant="lockup" iconSize={30} testid="trust-logo" />
        <Link
          to="/"
          data-testid="trust-back-home"
          className="text-xs text-zinc-500 hover:text-zinc-200 uppercase tracking-widest inline-flex items-center gap-2"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to home
        </Link>
      </header>

      <div className="relative z-10 max-w-6xl mx-auto px-8 py-14 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-12">
        {/* Sidebar nav */}
        <aside className="lg:sticky lg:top-8 lg:self-start">
          <div className="text-[10px] uppercase tracking-[0.28em] text-amber-400 mb-3">
            Trust &amp; Privacy
          </div>
          <nav className="space-y-1" data-testid="trust-nav">
            <NavLink
              to="/trust"
              end
              data-testid="trust-nav-home"
              className={({ isActive }) =>
                `block px-3 py-2 rounded-lg text-sm transition-colors ${
                  isActive || location.pathname === "/trust"
                    ? "bg-zinc-900 text-zinc-50 border border-zinc-800"
                    : "text-zinc-400 hover:text-zinc-200 hover:bg-zinc-900/50"
                }`
              }
            >
              Overview
            </NavLink>
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`trust-nav-${n.to.split("/").pop()}`}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActive
                      ? "bg-zinc-900 text-zinc-50 border border-zinc-800"
                      : "text-zinc-400 hover:text-zinc-200 hover:bg-zinc-900/50"
                  }`
                }
              >
                <n.icon className="h-3.5 w-3.5 text-amber-400/70" />
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="mt-8 p-3.5 rounded-lg border border-zinc-800 bg-zinc-900/40">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Questions?</div>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              data-testid="trust-contact-email"
              className="text-xs text-amber-400 hover:text-amber-300 inline-flex items-center gap-1.5 break-all"
            >
              <Mail className="h-3 w-3" /> {CONTACT_EMAIL}
            </a>
          </div>
        </aside>

        {/* Main */}
        <main data-testid={testid} className="min-w-0">
          {kicker && (
            <div className="text-[11px] uppercase tracking-[0.28em] text-amber-400 mb-3">{kicker}</div>
          )}
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-zinc-50">{title}</h1>
          <div className="text-xs text-zinc-500 mt-3">Last updated: {LAST_UPDATED}</div>
          <div className="mt-10 prose-invert max-w-none">{children}</div>
        </main>
      </div>

      {/* Footer */}
      <footer className="relative z-10 border-t border-zinc-900 mt-10">
        <div className="max-w-6xl mx-auto px-8 py-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-zinc-600">
          <span>© 2026 {COMPANY}</span>
          <div className="flex flex-wrap gap-4">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} className="hover:text-zinc-300">
                {n.label}
              </Link>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}

/* -------------------------- typography helpers ------------------------- */

function P({ children }) {
  return <p className="text-sm text-zinc-300 leading-relaxed mb-4">{children}</p>;
}
function Lead({ children }) {
  return <p className="text-base text-zinc-300 leading-relaxed mb-6">{children}</p>;
}
function H2({ children, id }) {
  return (
    <h2 id={id} className="text-lg font-semibold text-zinc-50 tracking-tight mt-10 mb-3">
      {children}
    </h2>
  );
}
function H3({ children }) {
  return <h3 className="text-sm font-semibold text-amber-300 uppercase tracking-widest mt-6 mb-2">{children}</h3>;
}
function UL({ items }) {
  return (
    <ul className="space-y-2 mb-4">
      {items.map((it, i) => (
        <li key={i} className="flex gap-2.5 text-sm text-zinc-300 leading-relaxed">
          <span className="mt-2 h-1 w-1 rounded-full bg-amber-400/70 flex-shrink-0" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}
function Callout({ children, variant = "default" }) {
  const styles = {
    default: "border-zinc-800 bg-zinc-900/40",
    warn: "border-amber-500/25 bg-amber-500/[0.04]",
  };
  return (
    <div className={`rounded-xl border ${styles[variant]} p-5 my-6 text-sm text-zinc-300 leading-relaxed`}>
      {children}
    </div>
  );
}
function NextPrev({ prev, next }) {
  return (
    <div className="mt-14 pt-6 border-t border-zinc-900 flex flex-col sm:flex-row gap-3 justify-between">
      {prev ? (
        <Link
          to={prev.to}
          className="group inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-zinc-100"
        >
          <ArrowLeft className="h-3.5 w-3.5 group-hover:-translate-x-0.5 transition-transform" />
          {prev.label}
        </Link>
      ) : <span />}
      {next ? (
        <Link
          to={next.to}
          className="group inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-zinc-100"
        >
          {next.label}
          <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
        </Link>
      ) : <span />}
    </div>
  );
}

/* =========================== Overview / Hub =========================== */

export function TrustOverview() {
  return (
    <TrustLayout
      testid="trust-overview"
      kicker="Trust & Privacy"
      title="Built with privacy and security at the core."
    >
      <Lead>
        {COMPANY} is designed with internationally recognized privacy and security principles and is
        continuously evolving to support applicable privacy regulations, including the EU GDPR,
        California CCPA/CPRA, the Philippine Data Privacy Act, and other applicable laws where required.
      </Lead>
      <P>
        This Trust &amp; Privacy center brings together the policies and practices that govern how we
        handle your information, how our AI works, and how you can exercise control over your data.
      </P>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-10">
        {NAV.map((n) => (
          <Link
            key={n.to}
            to={n.to}
            data-testid={`trust-card-${n.to.split("/").pop()}`}
            className="group p-5 rounded-xl border border-zinc-800 bg-zinc-900/40 hover:border-amber-500/30 hover:bg-zinc-900/60 transition-all"
          >
            <div className="flex items-start gap-3.5">
              <span className="h-10 w-10 rounded-lg bg-zinc-800/60 border border-zinc-800 flex items-center justify-center flex-shrink-0 group-hover:border-amber-500/30">
                <n.icon className="h-4 w-4 text-amber-400" />
              </span>
              <div>
                <div className="text-sm font-semibold text-zinc-50">{n.label}</div>
                <div className="text-xs text-zinc-500 mt-1 leading-relaxed">
                  {n.to === "/trust/privacy" && "What data we collect, why we collect it, and how we protect it."}
                  {n.to === "/trust/terms" && "Your responsibilities, acceptable use, and how our service works."}
                  {n.to === "/trust/security" && "Encryption, secure authentication, and responsible AI practices."}
                  {n.to === "/trust/cookies" && "The small files we use to keep the app working and improving."}
                  {n.to === "/trust/ai-transparency" && "How AI is used, its limitations, and how to review its output."}
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>

      <Callout variant="warn">
        <strong className="text-amber-300">A note on certifications.</strong> {COMPANY} does not
        currently hold formal certifications (such as SOC 2, ISO 27001, or GDPR certification).
        Instead, we align our practices to widely accepted privacy and security principles and expand
        our program as we grow. We&apos;ll update this page when meaningful milestones are reached.
      </Callout>
    </TrustLayout>
  );
}

/* ============================ Privacy Policy ========================== */

export function PrivacyPolicy() {
  return (
    <TrustLayout testid="trust-privacy" kicker="Privacy Policy" title="Privacy Policy">
      <Lead>
        This Privacy Policy explains how {COMPANY} collects, uses, shares, and protects information
        when you use our website and services (the &quot;Service&quot;). We designed {COMPANY} with
        internationally recognized privacy principles in mind and are continuously evolving to support
        applicable privacy regulations, including the EU GDPR, California CCPA/CPRA, the Philippine
        Data Privacy Act, and other laws where required.
      </Lead>

      <H2>1. What data we collect</H2>
      <H3>Account information</H3>
      <UL items={[
        "Name, email address, and password (hashed) or, if you use social sign-in, the profile information the provider shares with us.",
        "Profile photo, if provided by your identity provider.",
      ]} />
      <H3>Career &amp; interview content</H3>
      <UL items={[
        "Resumes you upload or paste (files and text).",
        "Job descriptions and role details you enter.",
        "Interview preferences, target role, career goal, industry, years of experience, and preferred language.",
        "Transcripts of AI mock interview sessions, questions predicted, drafted answers, coach notes, readiness scores, and Interview Day Briefs.",
        "Video and audio recordings of your interview answers, captured only while you are actively recording an answer in a Practice or Official AI Interview, together with their transcripts.",
      ]} />
      <H3>Usage &amp; device information</H3>
      <UL items={[
        "Basic technical details such as browser type, device model, IP address, and pages visited, collected to keep the Service reliable and secure.",
        "Product analytics on which features you use so we can prioritize improvements.",
      ]} />

      <H2>2. Why we collect it</H2>
      <UL items={[
        "To provide the Service, including resume alignment, question prediction, mock interviews, coaching, and readiness scoring.",
        "To personalize your experience based on your Career Goal, target role, and preferences.",
        "To secure your account, prevent abuse, and protect our infrastructure.",
        "To communicate with you about your account, product updates you have opted into, and transactional emails (such as verification, password reset).",
        "To improve the Service, measuring feature performance, fixing bugs, and refining AI quality.",
        "To meet legal obligations where they apply.",
      ]} />

      <H2>3. How AI processes your resumes and interview data</H2>
      <P>
        {COMPANY} uses large language models (LLMs) hosted by trusted third-party AI providers to
        power resume alignment, question prediction, Practice Interviews, coaching, and Interview
        Day Briefs. When you use these features:
      </P>
      <UL items={[
        "The relevant content (e.g., your resume text, job description, or interview turn) is sent to the AI provider strictly to generate the requested output.",
        "Interview answers are evaluated from their transcript plus speech metrics (duration, pace, filler words). We do not analyze facial expressions, eye contact, appearance, or emotions — your video recording is never sent to an AI model and plays no role in scoring.",
        "We do not sell your personal data. We do not use your personal data to train third-party foundation models beyond the scope of a single request.",
        "AI-generated output is stored on your account so you can revisit it. You can delete this data at any time from Settings → Privacy, or by contacting us.",
        "AI can make mistakes. See our AI Transparency page for a full explanation of limitations.",
      ]} />

      <H2>4. How data is stored and protected</H2>
      <UL items={[
        "Data is transmitted over encrypted channels (HTTPS/TLS).",
        "Interview video/audio recordings are stored in access-controlled object storage and are only served through your authenticated account. Shared reports contain scores and text summaries only — never your raw recordings.",
        "Passwords are hashed using industry-standard algorithms; we never store passwords in plaintext.",
        "Access to production data is limited to authorized personnel who require it to operate the Service.",
        "We monitor our systems for suspicious activity and apply security patches promptly.",
      ]} />
      <P>
        No online service can guarantee absolute security. If we discover a security incident that
        affects you, we will notify you in accordance with applicable law.
      </P>

      <H2>5. Your rights</H2>
      <P>
        Depending on where you live, you may have the following rights over your personal data:
      </P>
      <UL items={[
        "Access, request a copy of the personal data we hold about you.",
        "Correction, ask us to update or correct inaccurate data.",
        "Deletion, request that we delete your account and associated data.",
        "Portability, download your data in a machine-readable format.",
        "Objection or restriction, object to or restrict certain processing.",
        "Withdraw consent, where processing is based on consent, you can withdraw it at any time.",
        "Lodge a complaint, with your local data protection authority (such as the Information Commissioner's Office in the UK, a data protection authority in the EU, the California Privacy Protection Agency, or the National Privacy Commission of the Philippines).",
      ]} />
      <P>
        Most rights can be exercised directly from Settings → Privacy (download your data, delete
        interview history, delete your account). For anything else, email{" "}
        <a className="text-amber-400 hover:text-amber-300" href={`mailto:${CONTACT_EMAIL}`}>
          {CONTACT_EMAIL}
        </a>.
      </P>

      <H2>6. International data transfers</H2>
      <P>
        {COMPANY} is offered to users worldwide. Your data may be processed and stored in countries
        outside your own, including in jurisdictions whose data protection laws may differ from those
        in your country. When we transfer personal data internationally, we take steps to protect it
        consistent with applicable law, such as relying on recognized transfer mechanisms and
        contractual safeguards with our service providers.
      </P>

      <H2>7. Data retention</H2>
      <UL items={[
        "Account data is retained for as long as your account is active.",
        "Interview video/audio recordings, transcripts, mock sessions, and Interview Day Briefs are retained until you delete them or delete your account.",
        "When you delete your interview history or your account, the associated records are removed immediately and stored recording files are overwritten with empty content so they can no longer be retrieved.",
        "When you delete your account, we remove your personal data from active systems and initiate deletion from backup systems on our regular deletion schedule.",
        "We may retain limited information as required by law (for example, records related to security events, billing, or legal claims).",
      ]} />

      <H2>8. Cookies</H2>
      <P>
        We use a small number of cookies and similar technologies for essential functions like keeping
        you signed in, remembering your preferences, and understanding how the product is used. See
        our{" "}
        <Link className="text-amber-400 hover:text-amber-300" to="/trust/cookies">
          Cookie Policy
        </Link>{" "}
        for details.
      </P>

      <H2>9. Third-party services</H2>
      <P>
        We use trusted third-party providers to deliver the Service. These may include:
      </P>
      <UL items={[
        "AI providers that power resume analysis, question prediction, mock interviews, and coaching.",
        "Cloud infrastructure and database hosting.",
        "Authentication providers (for example, sign-in with Google).",
        "Email delivery (for verification, password reset, and product emails).",
        "Product analytics and error monitoring.",
      ]} />
      <P>
        These providers only receive the data necessary to perform their function and are contractually
        required to protect it.
      </P>

      <H2>10. Children</H2>
      <P>
        {COMPANY} is intended for users aged 16 and above (or the minimum age of digital consent in
        your country, whichever is higher). We do not knowingly collect personal data from children
        under this age. If you believe a child has provided us with personal data, please contact us
        and we will delete it.
      </P>

      <H2>11. Changes to this policy</H2>
      <P>
        We may update this Privacy Policy from time to time. When we do, we will update the &quot;Last
        updated&quot; date at the top of this page and, where changes are significant, notify you by
        email or in-product notice.
      </P>

      <H2>12. Contact us</H2>
      <P>
        For questions about this Privacy Policy or to exercise your rights, email{" "}
        <a className="text-amber-400 hover:text-amber-300" href={`mailto:${CONTACT_EMAIL}`}>
          {CONTACT_EMAIL}
        </a>.
      </P>

      <NextPrev
        prev={{ to: "/trust", label: "Trust & Privacy overview" }}
        next={{ to: "/trust/terms", label: "Terms of Service" }}
      />
    </TrustLayout>
  );
}

/* ============================= Terms of Service ======================= */

export function TermsOfService() {
  return (
    <TrustLayout testid="trust-terms" kicker="Terms of Service" title="Terms of Service">
      <Lead>
        These Terms of Service (&quot;Terms&quot;) govern your access to and use of {COMPANY} (the
        &quot;Service&quot;). By creating an account or using the Service, you agree to these Terms.
        If you do not agree, please do not use the Service.
      </Lead>

      <H2>1. Who can use {COMPANY}</H2>
      <UL items={[
        "You must be at least 16 years old (or the minimum age of digital consent in your country).",
        "You must provide accurate account information and keep it up to date.",
        "You are responsible for maintaining the confidentiality of your credentials and for all activity that occurs under your account.",
      ]} />

      <H2>2. Your responsibilities</H2>
      <UL items={[
        "Use the Service in compliance with all applicable laws.",
        "Only upload content you have the right to share (for example, your own resume).",
        "Review AI-generated content before relying on it for real-world decisions such as job applications or interviews.",
        "Do not misrepresent your identity, credentials, or work history.",
      ]} />

      <H2>3. Acceptable use</H2>
      <P>You agree not to:</P>
      <UL items={[
        "Attempt to access accounts or data belonging to others.",
        "Reverse engineer, scrape, or circumvent security controls of the Service.",
        "Use the Service to generate or distribute unlawful, defamatory, harassing, or infringing content.",
        "Attempt to overwhelm the Service through automated requests or denial-of-service techniques.",
        "Use the Service to build a competing product that copies our features, models, or interfaces.",
      ]} />

      <H2>4. AI limitations</H2>
      <P>
        {COMPANY} uses AI to help you prepare for interviews. AI can make mistakes, misinterpret
        context, or produce content that appears confident but is inaccurate. You are responsible for
        reviewing, editing, and verifying anything AI generates before using it externally. See our{" "}
        <Link className="text-amber-400 hover:text-amber-300" to="/trust/ai-transparency">
          AI Transparency page
        </Link>{" "}
        for a full explanation.
      </P>

      <H2>5. Intellectual property</H2>
      <H3>Your content</H3>
      <P>
        You retain ownership of the content you upload or create (your resumes, notes, and answers).
        You grant {COMPANY} a limited license to process and store that content solely to operate and
        improve the Service for you.
      </P>
      <H3>Our content</H3>
      <P>
        The {COMPANY} name, logo, product design, code, and all other materials we provide are owned
        by {COMPANY} or our licensors and are protected by intellectual property laws. You may not
        copy, modify, or redistribute them without our written permission.
      </P>
      <H3>AI-generated output</H3>
      <P>
        Subject to these Terms, you are free to use the AI-generated output {COMPANY} produces for
        you (for example, aligned resumes and Interview Day Briefs) in your own job search.
      </P>

      <H2>6. Subscription and billing</H2>
      <UL items={[
        "Some features may be available for free; others may require a paid subscription.",
        "Paid subscriptions renew automatically until cancelled from Settings → Subscription.",
        "Fees are billed in advance and are non-refundable except where required by law or expressly stated in our refund policy.",
        "We may change pricing with reasonable prior notice.",
      ]} />

      <H2>7. Termination</H2>
      <P>
        You may stop using the Service and delete your account at any time from Settings → Account.
        We may suspend or terminate accounts that violate these Terms or applicable law, or that
        pose a security or legal risk to the Service or its users.
      </P>

      <H2>8. Disclaimer</H2>
      <P className="uppercase text-xs tracking-widest text-zinc-400">
        The Service is provided &quot;as is&quot; and &quot;as available.&quot; To the fullest extent
        permitted by law, {COMPANY} disclaims all warranties, express or implied, including warranties
        of merchantability, fitness for a particular purpose, non-infringement, and any warranties
        regarding accuracy, reliability, or interview outcomes.
      </P>
      <P>
        {COMPANY} does not guarantee job offers, interview success, ATS pass rates, or any specific
        career outcome. Interview outcomes depend on many factors outside our control.
      </P>

      <H2>9. Limitation of liability</H2>
      <P className="uppercase text-xs tracking-widest text-zinc-400">
        To the fullest extent permitted by law, in no event will {COMPANY}, its affiliates, or their
        officers, employees, or agents be liable for any indirect, incidental, special, consequential,
        or punitive damages, or any loss of profits, revenue, data, or opportunities, arising out of
        or related to your use of the Service. Our total liability for any claim arising out of or
        related to the Service is limited to the amount you paid us for the Service in the twelve
        months preceding the event giving rise to the claim, or one hundred U.S. dollars, whichever
        is greater.
      </P>

      <H2>10. Changes to the Terms</H2>
      <P>
        We may update these Terms from time to time. If a change is material, we will notify you by
        email or in-product notice. Your continued use of the Service after changes take effect means
        you accept the updated Terms.
      </P>

      <H2>11. Contact</H2>
      <P>
        Questions about these Terms? Email{" "}
        <a className="text-amber-400 hover:text-amber-300" href={`mailto:${CONTACT_EMAIL}`}>
          {CONTACT_EMAIL}
        </a>.
      </P>

      <NextPrev
        prev={{ to: "/trust/privacy", label: "Privacy Policy" }}
        next={{ to: "/trust/security", label: "Security" }}
      />
    </TrustLayout>
  );
}

/* ============================== Security ============================= */

export function Security() {
  return (
    <TrustLayout testid="trust-security" kicker="Security" title="How we protect your data">
      <Lead>
        {COMPANY} is designed with internationally recognized security principles. We do not claim
        formal certifications we do not yet hold; instead, we describe the concrete practices we
        follow today, and we expand this program as we grow.
      </Lead>

      <H2>Encryption</H2>
      <UL items={[
        "Data in transit is protected with HTTPS/TLS between your browser and our servers, and between our servers and our service providers.",
        "Passwords are stored as one-way cryptographic hashes using industry-standard algorithms, we never store or transmit plaintext passwords.",
        "Sensitive tokens (session cookies, verification tokens) are cryptographically random and short-lived.",
      ]} />

      <H2>Secure authentication</H2>
      <UL items={[
        "You can sign in with email and password or with a supported social sign-in provider.",
        "Password requirements include a minimum length and are validated on account creation and password change.",
        "Verification emails and password reset links expire after a short window.",
        "Sessions are protected with secure, HTTP-only cookies to reduce exposure to common web attacks.",
      ]} />

      <H2>Account protection</H2>
      <UL items={[
        "You can update your password, sign out of all devices, and delete your account at any time from Settings.",
        "We monitor for suspicious sign-in activity and apply protections against brute-force attempts.",
        "Administrative access to production systems is limited to authorized personnel and audited.",
      ]} />

      <H2>Responsible AI practices</H2>
      <UL items={[
        "AI providers only receive the content necessary to produce the requested output.",
        "We do not sell your personal data. We do not use your personal data to train third-party foundation models beyond the scope of a single request.",
        "AI-generated content is stored on your account so you can review, edit, and delete it.",
        "See our AI Transparency page for how AI can go wrong and how to review it safely.",
      ]} />

      <H2>Data deletion</H2>
      <UL items={[
        "Delete individual mock sessions and Interview Day Briefs from within the app.",
        "Clear your full interview history — including video/audio recordings and transcripts — from Settings → Privacy. Stored recording files are overwritten with empty content.",
        "Delete your entire account and personal data from Settings → Account.",
        "Deletion from active systems is immediate; deletion from backup systems follows our regular retention schedule.",
      ]} />

      <H2>Report a security concern</H2>
      <P>
        We welcome responsible disclosure. If you believe you have found a vulnerability, please
        contact us at{" "}
        <a className="text-amber-400 hover:text-amber-300" href={`mailto:${CONTACT_EMAIL}`}>
          {CONTACT_EMAIL}
        </a>{" "}
        with steps to reproduce, potential impact, and any supporting materials. Please do not
        publicly disclose the issue until we have had a reasonable opportunity to investigate and
        respond.
      </P>

      <Callout>
        We&apos;re always improving. As our security program matures, we&apos;ll publish updates here
        rather than make unverifiable claims. Thank you for helping us keep {COMPANY} trustworthy.
      </Callout>

      <NextPrev
        prev={{ to: "/trust/terms", label: "Terms of Service" }}
        next={{ to: "/trust/cookies", label: "Cookie Policy" }}
      />
    </TrustLayout>
  );
}

/* ============================ Cookie Policy =========================== */

export function CookiePolicy() {
  return (
    <TrustLayout testid="trust-cookies" kicker="Cookie Policy" title="Cookie Policy">
      <Lead>
        This Cookie Policy explains how {COMPANY} uses cookies and similar technologies to operate
        our Service. It should be read alongside our Privacy Policy.
      </Lead>

      <H2>What are cookies?</H2>
      <P>
        Cookies are small text files placed on your device by websites you visit. They help sites
        remember information about your visit, such as whether you&apos;re signed in, and are
        widely used to make sites work reliably.
      </P>

      <H2>Categories of cookies we use</H2>
      <H3>Strictly necessary</H3>
      <P>
        Required for core functionality such as signing you in, keeping you signed in, and protecting
        against common security threats. These cannot be turned off without breaking the Service.
      </P>
      <H3>Functional</H3>
      <P>
        Remember choices you make (like theme, expanded sections, or language) so you don&apos;t have
        to set them every time.
      </P>
      <H3>Analytics</H3>
      <P>
        Help us understand which features are used and where users encounter friction, so we can
        improve the product. We aim to minimize what we collect and, where possible, to use
        privacy-friendly analytics.
      </P>

      <H2>Third-party cookies</H2>
      <P>
        Some cookies may be set by third parties we use to operate the Service, for example,
        authentication providers and error-monitoring tools. These parties act on our behalf and
        under our instructions.
      </P>

      <H2>Managing cookies</H2>
      <UL items={[
        "Most browsers let you view, delete, and block cookies through their settings.",
        "Blocking strictly necessary cookies may prevent you from signing in or using core features.",
        "You can also delete your account from Settings → Account, which removes your personal data associated with these cookies.",
      ]} />

      <H2>Changes to this policy</H2>
      <P>
        We may update this Cookie Policy to reflect changes in the way we use cookies or in
        applicable law. Updates will be posted on this page.
      </P>

      <NextPrev
        prev={{ to: "/trust/security", label: "Security" }}
        next={{ to: "/trust/ai-transparency", label: "AI Transparency" }}
      />
    </TrustLayout>
  );
}

/* ============================ AI Transparency ========================= */

export function AITransparency() {
  return (
    <TrustLayout testid="trust-ai" kicker="AI Transparency" title="How we use AI, and its limits">
      <Lead>
        {COMPANY} uses AI to help you prepare for interviews. We believe you should understand exactly
        how AI is used, where it can go wrong, and how to use its output responsibly.
      </Lead>

      <H2>How AI is used</H2>
      <UL items={[
        "Resume Alignment, analyzing your resume against a job description and suggesting refinements.",
        "ATS Optimization, highlighting keywords and formatting considerations that improve the chances your resume is parsed correctly.",
        "Cover Letter Drafting, generating a tailored cover letter from your aligned resume and the job description, in your choice of tone. You can edit every word before sending.",
        "Question Prediction, generating likely interview questions for your role, industry, and career goal.",
        "Answer Drafting, producing sample answers that you can adapt in your own voice.",
        "Practice Interview, a webcam-based mock interview with an AI persona; the model transcribes your answer, evaluates it against a rubric, and returns per-turn coaching notes.",
        "Official AI Interview, the flagship voice-based assessment that generates your Official Interview Readiness Score, hiring confidence level, strengths, and improvement areas.",
        "Interview Readiness Score, combining resume fit and mock performance into a directional readiness signal.",
        "Interview Day Brief, a printable summary of your top questions, stories, and reminders.",
      ]} />

      <H2>What the AI does — and does not — see</H2>
      <UL items={[
        "Interview assessment is based on the transcript of your spoken answers plus limited speech metrics: answer duration, speaking pace (words per minute), and filler-word counts.",
        "Intuiterview does not analyze facial expressions, eye contact, appearance, or emotions. Your video is never sent to an AI model.",
        "Video is used for interview simulation, recording, and replay only — it plays no role in scoring.",
        "Scores labeled Confidence, Leadership Presence, or Executive Presence are inferred from the content and delivery of your words, not from facial or emotion analysis.",
      ]} />

      <H2>AI can make mistakes</H2>
      <P>
        Modern AI models are powerful but imperfect. They can:
      </P>
      <UL items={[
        "Produce content that sounds confident but is factually wrong.",
        "Misinterpret nuance, tone, or specific technical context.",
        "Reflect biases present in their training data.",
        "Occasionally repeat or omit information.",
        "Generate content that requires human judgment to be truly appropriate for you.",
      ]} />

      <H2>Please review AI-generated content</H2>
      <P>
        Before using anything AI generates externally, sending an aligned resume, quoting a drafted
        answer, or relying on a predicted question set, please review it carefully. You know your
        story, your credentials, and your goals better than any model. AI is a coach and drafting
        partner, not a substitute for your judgment.
      </P>

      <H2>AI does not guarantee interview success</H2>
      <P>
        Using {COMPANY} does not guarantee a job offer, an interview invitation, an ATS pass, or any
        other career outcome. Interview success depends on many factors, your experience, your
        preparation, market conditions, and the specific hiring team&apos;s judgment. We build tools
        to give you a stronger, calmer starting position; you bring the rest.
      </P>

      <H2>Data and AI providers</H2>
      <UL items={[
        "AI providers receive only the content required to produce the requested output.",
        "We do not sell your personal data. We do not use your personal data to train third-party foundation models beyond the scope of a single request.",
        "AI output is stored on your account so you can review, edit, and delete it at any time.",
      ]} />

      <H2>Your controls</H2>
      <UL items={[
        "Change your Career Goal, coaching style, and difficulty from Settings → Career Goal and Settings → Interview Preferences to shape AI output.",
        "Download your data from Settings → Privacy → Download my data.",
        "Delete individual sessions, your interview history, or your entire account whenever you choose.",
      ]} />

      <Callout variant="warn">
        <strong className="text-amber-300">Bottom line.</strong> AI is powerful, but it&apos;s a
        drafting partner, not the final word. Always review, personalize, and verify before you use
        AI-generated content in a real interview or application.
      </Callout>

      <NextPrev
        prev={{ to: "/trust/cookies", label: "Cookie Policy" }}
        next={null}
      />
    </TrustLayout>
  );
}
